'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { createClient, getAuthRedirectUrl } from '@/lib/supabase/client';
import { mapAuthError, evaluatePasswordStrength } from '@/lib/auth-errors';
import PasswordChecklist from '@/components/PasswordChecklist';

function getEmailProviderUrl(email: string): { name: string; url: string } | null {
  const domain = email.split('@')[1]?.toLowerCase();
  if (!domain) return null;
  if (domain === 'gmail.com' || domain === 'googlemail.com') return { name: 'Gmail', url: 'https://mail.google.com' };
  if (domain === 'outlook.com' || domain === 'hotmail.com' || domain === 'live.com') return { name: 'Outlook', url: 'https://outlook.live.com' };
  if (domain === 'yahoo.com' || domain === 'ymail.com') return { name: 'Yahoo Mail', url: 'https://mail.yahoo.com' };
  if (domain === 'protonmail.com' || domain === 'proton.me' || domain === 'pm.me') return { name: 'Proton Mail', url: 'https://mail.proton.me' };
  if (domain === 'icloud.com' || domain === 'me.com' || domain === 'mac.com') return { name: 'iCloud Mail', url: 'https://www.icloud.com/mail' };
  if (domain === 'zoho.com') return { name: 'Zoho Mail', url: 'https://mail.zoho.com' };
  if (domain === 'rediffmail.com') return { name: 'Rediffmail', url: 'https://mail.rediff.com' };
  return null;
}

export default function SignupPage() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isPasswordFocused, setIsPasswordFocused] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [verifyEmail, setVerifyEmail] = useState('');
  const [resending, setResending] = useState(false);
  const [resendDone, setResendDone] = useState(false);

  const supabase = createClient();
  const strength = evaluatePasswordStrength(password);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!email || !password) {
      setError('Please fill in all required fields.');
      return;
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters long.');
      return;
    }

    if (strength.score < 3) {
      setError('Please choose a stronger password matching at least 3 checklist requirements.');
      return;
    }

    setLoading(true);
    setError('');

    if (typeof window !== 'undefined') {
      localStorage.setItem('kheldrop_last_signup_email', email.trim().toLowerCase());
    }

    try {
      const emailRedirectTo = getAuthRedirectUrl('/auth/callback');

      const { data, error: signUpError } = await supabase.auth.signUp({
        email: email.trim().toLowerCase(),
        password,
        options: {
          data: {
            full_name: name.trim() || email.split('@')[0],
          },
          emailRedirectTo,
        },
      });

      if (signUpError) {
        const mapped = mapAuthError(signUpError.message);
        setError(mapped.message);
        return;
      }

      if (data.session) {
        window.location.href = '/';
      } else {
        setVerifyEmail(email.trim().toLowerCase());
      }
    } catch (err) {
      const mapped = mapAuthError(err);
      setError(mapped.message);
    } finally {
      setLoading(false);
    }
  }

  async function handleGoogleSignIn() {
    setLoading(true);
    setError('');

    try {
      const { error: oauthError } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: getAuthRedirectUrl('/auth/callback'),
        },
      });

      if (oauthError) {
        const mapped = mapAuthError(oauthError.message);
        setError(mapped.message);
        setLoading(false);
      }
    } catch (err) {
      const mapped = mapAuthError(err);
      setError(mapped.message);
      setLoading(false);
    }
  }

  async function handleResend() {
    if (!verifyEmail || resending) return;
    setResending(true);
    setResendDone(false);
    try {
      await supabase.auth.resend({
        type: 'signup',
        email: verifyEmail,
        options: {
          emailRedirectTo: getAuthRedirectUrl('/auth/callback'),
        },
      });
      setResendDone(true);
    } catch {
      setResendDone(true);
    } finally {
      setResending(false);
    }
  }

  if (verifyEmail) {
    const provider = getEmailProviderUrl(verifyEmail);
    return (
      <div style={{ textAlign: 'center' }}>
        <div className="auth-success-icon">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
            <polyline points="22,6 12,13 2,6" />
          </svg>
        </div>

        <h2 className="auth-success-title">Verify your email</h2>

        <p className="auth-success-text">
          We sent a verification link to{' '}
          <span className="auth-email-highlight">{verifyEmail}</span>.
          Click the link to activate your account and start claiming slots.
        </p>

        <p style={{ fontSize: '13px', color: 'var(--text-muted)', margin: '0 0 20px', lineHeight: 1.5 }}>
          Check your spam or junk folder if you don't see it within a few minutes.
        </p>

        {provider && (
          <a
            href={provider.url}
            target="_blank"
            rel="noopener noreferrer"
            className="auth-submit"
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              marginBottom: '12px',
              textDecoration: 'none',
            }}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
              <polyline points="15 3 21 3 21 9" />
              <line x1="10" y1="14" x2="21" y2="3" />
            </svg>
            Open {provider.name}
          </a>
        )}

        {resendDone ? (
          <div className="auth-alert auth-alert-success" style={{ textAlign: 'left', marginBottom: '16px' }}>
            <svg className="auth-alert-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="20 6 9 17 4 12" />
            </svg>
            <span>A fresh verification link has been sent.</span>
          </div>
        ) : (
          <button
            type="button"
            className="auth-oauth-btn"
            onClick={handleResend}
            disabled={resending}
            style={{ marginBottom: '16px' }}
          >
            {resending ? (
              <>
                <span className="auth-spinner" />
                Resending verification email…
              </>
            ) : (
              'Resend Verification Email'
            )}
          </button>
        )}

        <div className="auth-footer" style={{ marginTop: '20px' }}>
          Back to{' '}
          <Link href="/login">Sign in</Link>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="auth-card-header">
        <h2>Join KhelDrop</h2>
        <p>Create your account to join games and track your match reputation.</p>
      </div>

      {error && (
        <div className="auth-alert auth-alert-error">
          <svg className="auth-alert-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10" />
            <line x1="15" y1="9" x2="9" y2="15" />
            <line x1="9" y1="9" x2="15" y2="15" />
          </svg>
          <span>{error}</span>
        </div>
      )}

      <button
        type="button"
        className="auth-oauth-btn"
        onClick={handleGoogleSignIn}
        disabled={loading}
      >
        <svg width="20" height="20" viewBox="0 0 24 24">
          <path fill="#4285F4" d="M23.745 12.27c0-.7-.06-1.4-.19-2.07H12v4.51h6.6c-.29 1.52-1.14 2.8-2.4 3.66v3.05h3.88c2.27-2.09 3.665-5.17 3.665-9.15z" />
          <path fill="#34A853" d="M12 24c3.24 0 5.95-1.08 7.93-2.91l-3.88-3.05c-1.08.72-2.45 1.16-4.05 1.16-3.12 0-5.77-2.1-6.72-4.93H1.25v3.15C3.26 21.36 7.33 24 12 24z" />
          <path fill="#FBBC05" d="M5.28 14.27c-.25-.72-.38-1.49-.38-2.27s.13-1.55.38-2.27V6.58H1.25C.45 8.18 0 9.99 0 12s.45 3.82 1.25 5.42l4.03-3.15z" />
          <path fill="#EA4335" d="M12 4.75c1.77 0 3.35.61 4.6 1.8l3.42-3.42C17.95 1.19 15.24 0 12 0 7.33 0 3.26 2.64 1.25 6.58l4.03 3.15c.95-2.83 3.6-4.98 6.72-4.98z" />
        </svg>
        Sign up with Google
      </button>

      <div className="auth-divider">
        <div className="auth-divider-line" />
        <span className="auth-divider-text">or with email</span>
        <div className="auth-divider-line" />
      </div>

      <form onSubmit={handleSubmit}>
        <div className="auth-form-group">
          <label className="auth-label" htmlFor="signup-name">Full Name</label>
          <input
            id="signup-name"
            className="auth-input"
            type="text"
            placeholder="Alex Sharma"
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={loading}
            autoComplete="name"
          />
        </div>

        <div className="auth-form-group">
          <label className="auth-label" htmlFor="signup-email">Email address</label>
          <input
            id="signup-email"
            className="auth-input"
            type="email"
            placeholder="you@domain.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={loading}
            autoComplete="email"
            required
          />
        </div>

        <div className="auth-form-group">
          <label className="auth-label" htmlFor="signup-password">Password</label>
          <div className="auth-password-wrapper">
            <input
              id="signup-password"
              className="auth-input"
              type={showPassword ? 'text' : 'password'}
              placeholder="Create a strong password (min 8 chars)"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onFocus={() => setIsPasswordFocused(true)}
              onBlur={() => setIsPasswordFocused(false)}
              disabled={loading}
              autoComplete="new-password"
              required
            />
            <button
              type="button"
              className="auth-password-toggle"
              onClick={() => setShowPassword(!showPassword)}
              tabIndex={-1}
            >
              {showPassword ? 'Hide' : 'Show'}
            </button>
          </div>

          <PasswordChecklist
            password={password}
            alwaysShow={true}
          />
        </div>

        <button type="submit" className="auth-submit" disabled={loading}>
          {loading ? (
            <>
              <span className="auth-spinner" />
              Creating account…
            </>
          ) : (
            'Create Account'
          )}
        </button>
      </form>

      <div className="auth-footer">
        Already have an account?{' '}
        <Link href="/login">Sign in</Link>
      </div>
    </>
  );
}

